import { IObservable, Observable } from "thrive-observable-react"
import { getChatByUserId, getChats, getMyUserProfile } from "../api"
import PollingService from "./Polling.Service"

import { IChatMessage, IChatMeta, IPaginatedResponse, IUser } from "../interfaces"
import { ConversationService } from "./ChatConversation.Service"
import { CAS } from "./ChatConfig.Service"


class ChatService {

    public me:IObservable<IUser> =  Observable<IUser>({} as IUser)
    
    private longPollForUpdates!: PollingService

    public lastPoll!: Date

    public chats: IObservable<IChatMeta[]> = Observable<IChatMeta[]>([]);

    public totalChats: IObservable<number> = Observable<number>(0);

    public newChatsIsOpen: IObservable<boolean> = Observable<boolean>(false);

    public isLoadingMore: IObservable<boolean> = Observable<boolean>(false);

    public initialChatLoaded: IObservable<boolean> = Observable<boolean>(false);

    
    private paginationAbortController: AbortController | null = null;

    public init() : void {
        this.longPollForUpdates = new PollingService();

        const pollerOptions = {
            request: (ifModifiedSince: string) => getChats(ifModifiedSince, this.getTopParams()),
            onSuccess:  (data: IPaginatedResponse<IChatMeta[]>) => {
                this.chats.update(data.items);
                this.totalChats.update(data.totalItemCount);
                
                // scroll chat to the top if we get a new message while we are looking at chat
                this.scrollChatToTop();
            },
            onError: (err: any) => console.error(err),
            config: {
                timeout: 5000,
                stopPollingOnError: true
            }
        }

        this.longPollForUpdates.startPoller(pollerOptions);
    }

    public getInitialChats(){

        if(this.initialChatLoaded.value) {
            return;
        }
        if(!this.chats.value.length) {
            getChats('').then(({data}: {data: IPaginatedResponse<IChatMeta[]>}) => {  
                this.chats.update(data.items);
                this.totalChats.update(data.totalItemCount);
            })
            .finally(() => {
                this.initialChatLoaded.update(true);
            })

            getMyUserProfile()?.then(({data}) => {
                this.me.update(data)
            })
        }
    }

    public refreshChats(){
        getChats('', {top: this.chats.value.length})
        .then(({data}: {data: IPaginatedResponse<IChatMeta[]>}) => {
            this.chats.update(data.items);
            this.totalChats.update(data.totalItemCount);
        })
    }

    public async getChatId(userId: number): Promise<number> {
        const { data } = await getChatByUserId(userId).catch(err => err);
        if(!data?.items?.length) {
            return 0
        }

        return data.items[0].id;
    }

    public getTopParams() {
        const chatPage = Math.ceil(this.chats.value.length / 20) * 20
        return {
            top: chatPage
        }
    }

    public getChatRecipientFromUsers(chatUsers: IUser[]): IUser {
        const otherUser = chatUsers.find(u => u.id !== this.me.value.id) as IUser
        
        // if there is another user return it
        if(otherUser) {
            return otherUser
        }

        //otherwise its a conversation with myself in which case return me
        return chatUsers[0];
        
    }

    public showNewChatPlaceholder(){
        document.querySelector('#chat-menu-item-0')?.classList.add('show');
        this.newChatsIsOpen.update(true);
    }

    public hideNewChatPlaceholder(){
        document.querySelector('#chat-menu-item-0')?.classList.remove('show');
         this.newChatsIsOpen.update(false);
    }

    public removeDuplicatedChatsAndPrependNewChatData(newChatData: IChatMeta[]){
        const newChatIds = newChatData.map(c => c.id)
        
        const filteredChats = this.chats.value.filter(c => !newChatIds.includes(c.id));
        return [...newChatData, ...filteredChats]
    }

    public resetChatService(){
        this.chats.update([]);
    }

    public updateMessageWithLastMessage(chatMessage: IChatMessage, chatId:number) {
        const targetChat = this.chats.value.find(c => c.id === chatId);
        const filteredChats = this.chats.value.filter(c => c.id !== chatId);

        this.chats.update([{...targetChat, lastMessage: chatMessage}, ...filteredChats])
        this.scrollChatToTop();
    }

    public getNextPageOfChats() {
        if (this.isLoadingMore.value || this.paginationAbortController) return;
        
        this.isLoadingMore.update(true);
        
        this.paginationAbortController = new AbortController();
        
        const signal = this.paginationAbortController.signal;
                
        getChats('', {top: this.chats.value.length + 20}, signal)
        .then(({data}) => {
            this.chats.update(data.items);
            this.totalChats.update(data.totalItemCount);
        })
        .catch((error) => {
            console.error(`People Search Paginateion error`, error);
        })
        .finally(() => {
            this.isLoadingMore.update(false);
            this.paginationAbortController = null;
        });
    }

    public hasMoreChats(){
        return this.chats.value.length < this.totalChats.value;
    }

    public resestService(){
        this.me.update({} as IUser)
    
        this.chats.update([])

        this.totalChats.update(0);

        this.newChatsIsOpen.update(false);

        this.isLoadingMore.update(false);
        
        this.initialChatLoaded.update(false);
    }

    public cleanUp(){
        this.resestService();
        ConversationService.resetService();
        this?.longPollForUpdates?.endPoller();
        CAS.cleanup();

    }

    public scrollChatToTop() {
        if(document.querySelector('#chats-menu .simplebar-content-wrapper')) {
            document?.querySelector?.('#chats-menu .simplebar-content-wrapper')?.scrollTo?.({
                top: 0,
                left: 0,
                behavior: "smooth"
            });
        };
    }
}

export const CS:ChatService = new ChatService();